'use client';

import cn from '@sindresorhus/class-names';
import {useRouter} from 'next/navigation';
import {memo, useCallback, useEffect, useMemo} from 'react';

import {
  AppCategory,
  AppsResponse,
  AppType,
  NO_CATEGORY_CATEGORY,
} from '@web-catalog/types';

import {
  useBreadcrumbsBackstackForCategory,
  useCategories,
} from '@web-catalog/hooks';

import {ROUTES} from '@web-catalog/routes';

import {Breadcrumbs} from '@web-catalog/ui-kit/Breadcrumbs';
import {Text} from '@web-catalog/ui-kit/Text';

import {AppsWithPagination} from '../AppsWithPagination';
import {ViewReporter} from '../NewMainPage/parts';
import styles from './MainPageContent.module.scss';

type Props = {
  appType: AppType;
  category?: string;
  page: number;
  appsList: AppsResponse;
};

const titleMap = {
  [AppType.MAIN]: 'Приложения',
  [AppType.GAMES]: 'Игры',
} as const;

const getPageTitle = (appType: AppType, activeCategory: AppCategory | null) =>
  titleMap[appType] +
  (activeCategory ? `: ${activeCategory.categoryName}` : '');

export const MainPageContent = memo<Props>(
  ({appType, category, page, appsList}) => {
    const router = useRouter();

    const {activeCategory} = useCategories(appType === AppType.GAMES);

    const breadcrumbsBackstack = useBreadcrumbsBackstackForCategory({
      category: activeCategory,
      isGamesPage: appType === AppType.GAMES,
    });

    const showBreadcrumbs = breadcrumbsBackstack.length > 0;

    const currentLocationTitle = useMemo(
      () => activeCategory?.categoryName || titleMap[appType],
      [activeCategory?.categoryName, appType],
    );

    const listName = [
      appType === AppType.MAIN ? 'Приложения' : 'Игры',
      activeCategory?.categoryName ??
        (appType === AppType.MAIN ? 'Все приложения' : 'Все игры'),
    ].join('/');

    useEffect(() => {
      document.documentElement.scrollTop = 0;
    }, [listName]);

    const onPageChange = useCallback(
      (page: number) => {
        const getRoute =
          appType === AppType.MAIN ? ROUTES.root.page : ROUTES.games.page;

        router.push(
          getRoute({
            category,
            page,
          }),
        );
      },
      [appType, category, router],
    );

    return (
      <>
        <div
          className={cn(
            styles.headingContainer,
            showBreadcrumbs && styles.withBreadcrumbs,
          )}
        >
          <ViewReporter
            appType={appType}
            categoryName={category || NO_CATEGORY_CATEGORY}
          />
          {showBreadcrumbs && (
            <Breadcrumbs
              currentLocationTitle={currentLocationTitle}
              backstack={breadcrumbsBackstack}
              className={styles.breadcrumbs}
            />
          )}

          <Text className={styles.heading} typography="h1" as="h1">
            {getPageTitle(appType, activeCategory)}
          </Text>
        </div>

        <AppsWithPagination
          appsResponse={appsList}
          pageNumber={page}
          onPageChange={onPageChange}
          listName={listName}
          allowAds
        />
      </>
    );
  },
);

MainPageContent.displayName = 'MainPageContent';
